'use client';

import { c as _c } from "react/compiler-runtime";
import { useApi } from '@repo/api';
import type { CommandWorkflowNode } from '@repo/shortcuts/command';
import { useMutation } from '@repo/tanstack/query';
import { getLoadStatusIconClass, toast } from '@repo/ui/components';
import { Archive, ArrowUpDown, Circle, CircleX, CopyPlus, Eye, History, MessageSquareText } from '@repo/ui/icons';
import { useAccess } from '@repo/auth';
import { permissions } from '@repo/permissions';
import { useMemo } from 'react';
import { useLoadContext } from '@/app/@protected/loads/[id]/(details)/_components/load-context/context.tsx';
import { useDuplicate } from '@/app/@protected/loads/_components/duplicate/duplicate-provider.tsx';
import { useLoadStatuses } from '@/app/@protected/loads/_hooks/use-load-statuses.ts';
import { canDuplicateLoad } from '@/app/@protected/loads/_utils/load-creation-access.ts';
import { useLoadCreationAccess } from '@/app/@protected/loads/_utils/use-load-creation-access.ts';
import { CommandLabel } from './command-label.tsx';
import { usePageCommands } from './command-provider.tsx';
type LoadData = ReturnType<typeof useLoadContext>['data'];

/** The "Duplicate load" action, which `ScopedCmdKShortcuts` also runs for `L D`. */
export const LOAD_DUPLICATE_COMMAND_ID = 'load-duplicate';
const getStatusCommandIcon = (alias: string) => {
  if (alias === 'archived') return Archive;
  if (alias === 'cancelled') return CircleX;
  return Circle;
};
const useUpdateLoadStatus = loadId => {
  const $ = _c(4);
  const api = useApi();
  let t0;
  if ($[0] !== api.loads.get.full || $[1] !== api.loads.updateStatus || $[2] !== loadId) {
    t0 = api.loads.updateStatus.mutationOptions({
      path: {
        id: loadId
      }
    }, {
      meta: {
        invalidates: [["loads"], api.loads.get.full.queryKey({
          path: {
            id: loadId
          }
        })]
      },
      onSuccess: _temp
    });
    $[0] = api.loads.get.full;
    $[1] = api.loads.updateStatus;
    $[2] = loadId;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return useMutation(t0);
};
export const LoadCommandContributor = () => {
  const $ = _c(8);
  const {
    data
  } = useLoadContext();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      permissions: [permissions.loads.updateStatus]
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    allowed: canUpdateStatus
  } = useAccess(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      permissions: [permissions.loads.list]
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    allowed: canListStatuses
  } = useAccess(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      permissions: [permissions.loads.historyView]
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    allowed: canViewHistory
  } = useAccess(t2);
  let t3;
  if ($[3] !== canListStatuses || $[4] !== canUpdateStatus || $[5] !== canViewHistory || $[6] !== data) {
    t3 = <LoadCommandContributorShell canListStatuses={canListStatuses} canUpdateStatus={canUpdateStatus} canViewHistory={canViewHistory} data={data} data-sentry-element="LoadCommandContributorShell" data-sentry-component="LoadCommandContributor" data-sentry-source-file="load-commands.tsx" />;
    $[3] = canListStatuses;
    $[4] = canUpdateStatus;
    $[5] = canViewHistory;
    $[6] = data;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
const LoadCommandContributorShell = t0 => {
  const $ = _c(8);
  const {
    canListStatuses,
    canUpdateStatus,
    canViewHistory,
    data
  } = t0;
  if (canListStatuses) {
    let t1;
    if ($[0] !== canUpdateStatus || $[1] !== canViewHistory || $[2] !== data) {
      t1 = <LoadCommandContributorWithStatuses canUpdateStatus={canUpdateStatus} canViewHistory={canViewHistory} data={data} />;
      $[0] = canUpdateStatus;
      $[1] = canViewHistory;
      $[2] = data;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  let t1;
  if ($[4] !== canUpdateStatus || $[5] !== canViewHistory || $[6] !== data) {
    t1 = <LoadCommandContributorContent canListStatuses={false} canUpdateStatus={canUpdateStatus} canViewHistory={canViewHistory} data={data} data-sentry-element="LoadCommandContributorContent" data-sentry-component="LoadCommandContributorShell" data-sentry-source-file="load-commands.tsx" />;
    $[4] = canUpdateStatus;
    $[5] = canViewHistory;
    $[6] = data;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
};
const LoadCommandContributorWithStatuses = t0 => {
  const $ = _c(5);
  const {
    canUpdateStatus,
    canViewHistory,
    data
  } = t0;
  const {
    data: statuses
  } = useLoadStatuses();
  let t1;
  if ($[0] !== canUpdateStatus || $[1] !== canViewHistory || $[2] !== data || $[3] !== statuses) {
    t1 = <LoadCommandContributorContent canListStatuses={true} canUpdateStatus={canUpdateStatus} canViewHistory={canViewHistory} data={data} statuses={statuses} data-sentry-element="LoadCommandContributorContent" data-sentry-component="LoadCommandContributorWithStatuses" data-sentry-source-file="load-commands.tsx" />;
    $[0] = canUpdateStatus;
    $[1] = canViewHistory;
    $[2] = data;
    $[3] = statuses;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
const LoadCommandContributorContent = t0 => {
  const $ = _c(39);
  const {
    canListStatuses,
    canUpdateStatus,
    canViewHistory,
    data,
    statuses
  } = t0;
  const {
    isPending,
    mutate
  } = useUpdateLoadStatus(data.id);
  const t1 = useLoadCreationAccess();
  let t2;
  if ($[0] !== data.status.alias || $[1] !== t1) {
    t2 = canDuplicateLoad(t1, data.status.alias);
    $[0] = data.status.alias;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const canDuplicate = t2;
  const {
    openDuplicate,
    sessionOpen
  } = useDuplicate();
  let t3;
  if ($[3] !== data.status.alias) {
    t3 = ["cancelled", ...(data.status.alias !== "completed" ? ["archived"] : [])];
    $[3] = data.status.alias;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const disabledStatuses = t3;
  const t4 = `Load #${data.id}`;
  const t5 = `/loads/${data.id}/overview`;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <CommandLabel icon={Eye}>Go to Overview</CommandLabel>;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== t5) {
    t7 = {
      href: t5,
      id: "load-overview",
      kind: "link" as const,
      label: t6,
      value: "load-overview"
    };
    $[6] = t5;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== canViewHistory || $[9] !== data.id) {
    t8 = canViewHistory ? [{
      href: `/loads/${data.id}/history`,
      id: "load-history",
      kind: "link" as const,
      label: <CommandLabel icon={History}>Go to History</CommandLabel>,
      value: "load-history"
    } satisfies CommandWorkflowNode] : [];
    $[8] = canViewHistory;
    $[9] = data.id;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const t9 = `/loads/${data.id}/overview?chat=open`;
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <CommandLabel icon={MessageSquareText}>Go to Internal messages</CommandLabel>;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== t9) {
    t11 = {
      href: t9,
      id: "load-internal-chat",
      kind: "link" as const,
      label: t10,
      value: "load-internal-chat"
    };
    $[12] = t9;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== canListStatuses || $[15] !== canUpdateStatus) {
    t12 = canUpdateStatus && canListStatuses ? [{
      id: "load-change-status",
      kind: "page" as const,
      label: <CommandLabel icon={ArrowUpDown}>Change status</CommandLabel>,
      pageId: "load-status",
      shortcut: "L S",
      value: "load-change-status"
    }] : [];
    $[14] = canListStatuses;
    $[15] = canUpdateStatus;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== canDuplicate || $[18] !== data.id || $[19] !== openDuplicate || $[20] !== sessionOpen) {
    t13 = canDuplicate ? [{
      disabled: sessionOpen,
      id: LOAD_DUPLICATE_COMMAND_ID,
      kind: "action" as const,
      label: <CommandLabel icon={CopyPlus}>Duplicate load</CommandLabel>,
      onSelect: () => openDuplicate(data.id),
      shortcut: "L D",
      value: LOAD_DUPLICATE_COMMAND_ID
    }] : [];
    $[17] = canDuplicate;
    $[18] = data.id;
    $[19] = openDuplicate;
    $[20] = sessionOpen;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t11 || $[23] !== t12 || $[24] !== t13 || $[25] !== t7 || $[26] !== t8) {
    t14 = [t7, ...t8, t11, ...t12, ...t13];
    $[22] = t11;
    $[23] = t12;
    $[24] = t13;
    $[25] = t7;
    $[26] = t8;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const t15 = t14 satisfies CommandWorkflowNode[];
  let t16;
  if ($[28] !== t15 || $[29] !== t4) {
    t16 = [{
      heading: t4,
      id: "load-detail-group",
      items: t15
    }];
    $[28] = t15;
    $[29] = t4;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const groups = t16;
  let t17;
  if ($[31] !== canListStatuses || $[32] !== canUpdateStatus || $[33] !== data.status.alias || $[34] !== disabledStatuses || $[35] !== isPending || $[36] !== mutate || $[37] !== statuses) {
    t17 = canUpdateStatus && canListStatuses && statuses ? {
      "load-status": {
        groups: [{
          id: "statuses",
          items: statuses.filter(_temp2).map(status_0 => ({
            disabled: isPending || disabledStatuses.includes(status_0.alias) || status_0.alias === data.status.alias,
            id: `status-${status_0.alias}`,
            kind: "action" as const,
            label: <CommandLabel icon={getStatusCommandIcon(status_0.alias)} iconClassName={getLoadStatusIconClass(status_0.alias)}>{status_0.title}</CommandLabel>,
            onSelect: () => {
              if (!isPending && status_0.alias !== data.status.alias) {
                mutate({
                  status: status_0.alias
                });
              }
            },
            value: `status-${status_0.alias}`
          }))
        }],
        inputPlaceholder: "Search statuses...",
        title: "Change Status"
      }
    } : undefined;
    $[31] = canListStatuses;
    $[32] = canUpdateStatus;
    $[33] = data.status.alias;
    $[34] = disabledStatuses;
    $[35] = isPending;
    $[36] = mutate;
    $[37] = statuses;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  const nestedPages = t17;
  usePageCommands("load-detail", groups, nestedPages);
  return null;
};
function _temp() {
  toast.success("Status updated successfully");
}
function _temp2(status) {
  return status.alias !== "draft";
}